import * as v from "valibot";
import { AxiosRESTDataSource, type RESTResponse } from "@kce2/common/api/v2/rest-data-source";
import { axiosInstance } from "@kce2/app/axios";
import { makeProxyResponseSchema } from "../schema/k8s-resource/proxy-cluster-resource";

const Version = "2023-01-01";
const Action = "ProxyClusterResource";

/**
 * ProxyClusterResource 请求构造工具类。
 *
 * 与 EnvoyGatewayApi 共享同一后端动作机制，但作为独立类提供，
 * 不引入共享基类、不改动 envoy-gateway 模块（零回归）。
 * 详见 docs/adr/0001-proxy-cluster-resource-as-utility-not-base-class.md。
 *
 * 泛型约束用 valibot 的 GenericSchema：这里是 valibot schema 的构建现场
 * （v.object/v.array 等 valibot API），与特定库绑定；
 * 构建出的 schema 实现了 StandardSchemaV1，传给 this.get() 时自动满足其约束。
 */
export class K8sResourceProxy extends AxiosRESTDataSource {
  baseURL = "/api/cluster";

  /** list 查询的 Data 形状：K8s ListResponse = { items: ItemSchema[] } */
  private static listDataSchema<S extends v.GenericSchema>(itemSchema: S) {
    return v.object({
      apiVersion: v.optional(v.string()),
      kind: v.optional(v.string()),
      items: v.optional(v.array(itemSchema)),
    });
  }

  /**
   * 查询全量资源列表 (GET)，Data 由传入的 item schema 类型化。
   * 返回的 res.data.Data.items 为类型化的 ItemSchema[]。
   */
  listResources<S extends v.GenericSchema>(params: {
    region: string;
    clusterId: string;
    apiVersion: string;
    resourceType: string;
    namespace?: string;
    fieldSelector?: string;
    itemSchema: S;
  }) {
    const { region, clusterId, apiVersion, resourceType, namespace, fieldSelector, itemSchema } =
      params;
    const responseSchema = makeProxyResponseSchema(K8sResourceProxy.listDataSchema(itemSchema));
    return this.get("", responseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: apiVersion,
        ResourceType: resourceType,
        ...(namespace && { Namespace: namespace }),
        ...(fieldSelector && { fieldSelector }),
      },
    }) as Promise<RESTResponse<typeof responseSchema>>;
  }

  /**
   * 查询单个资源 (GET)，Data 由传入的 item schema 类型化。
   * 返回的 res.data.Data 为单个类型化 ItemSchema。
   */
  getResource<S extends v.GenericSchema>(params: {
    region: string;
    clusterId: string;
    apiVersion: string;
    resourceType: string;
    name: string;
    namespace: string;
    editView?: boolean;
    itemSchema: S;
  }) {
    const { region, clusterId, apiVersion, resourceType, name, namespace, editView, itemSchema } =
      params;
    const responseSchema = makeProxyResponseSchema(itemSchema);
    return this.get("", responseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: apiVersion,
        ResourceType: resourceType,
        ResourceName: name,
        Namespace: namespace,
        EditView: editView,
      },
    }) as Promise<RESTResponse<typeof responseSchema>>;
  }
}

/** 默认实例，供 IngressApi/SecretApi 组合使用 */
export const k8sResourceProxy = new K8sResourceProxy(axiosInstance);
